import React, { useEffect, useState } from 'react';
import { adminAPI } from '../services/api';

function formatDate(value) {
  if (!value) return 'Never';
  return new Date(value).toLocaleString();
}

export default function AnnouncePanel() {
  const [type, setType] = useState('announcement');
  const [title, setTitle] = useState('A message from StrapIn');
  const [message, setMessage] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [targetScope, setTargetScope] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [durationHours, setDurationHours] = useState(24);
  const [maxDisplays, setMaxDisplays] = useState(3);
  const [campaigns, setCampaigns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadCampaigns = async () => {
    const response = await adminAPI.getAnnouncements();
    setCampaigns(response.data.notices || []);
  };

  useEffect(() => { loadCampaigns().catch(() => {}); }, []);

  useEffect(() => {
    if (targetScope !== 'selected' || search.trim().length < 2) {
      setSearchResults([]);
      return undefined;
    }
    const timer = window.setTimeout(async () => {
      try {
        const response = await adminAPI.searchAnnouncementRecipients(search.trim());
        setSearchResults(response.data.users || []);
      } catch (_) {
        setSearchResults([]);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search, targetScope]);

  const addUser = (user) => {
    setSelectedUsers((current) => current.some((item) => item._id === user._id) ? current : [...current, user]);
    setSearch('');
    setSearchResults([]);
  };

  const submit = async () => {
    const normalizedDuration = Number(durationHours);
    const normalizedDisplays = Number(maxDisplays);
    if (type === 'announcement' && (!Number.isFinite(normalizedDuration) || normalizedDuration < 1 || normalizedDuration > 8760)) {
      setError('Duration must be between 1 and 8760 hours.');
      return;
    }
    if (type === 'announcement' && (!Number.isInteger(normalizedDisplays) || normalizedDisplays < 1 || normalizedDisplays > 100)) {
      setError('Display count must be a whole number between 1 and 100.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await adminAPI.sendAnnouncement({
        type,
        title: title.trim(),
        message: message.trim(),
        targetScope,
        userIds: selectedUsers.map((user) => user._id),
        ...(type === 'announcement' ? {
          imageUrl: imageUrl.trim() || undefined,
          linkUrl: linkUrl.trim() || undefined,
          durationHours: normalizedDuration,
          maxDisplays: normalizedDisplays,
        } : {}),
      });
      setMessage('');
      setImageUrl('');
      setLinkUrl('');
      setSelectedUsers([]);
      await loadCampaigns();
    } catch (err) {
      const apiError = err.response?.data;
      setError(apiError?.error || apiError?.message || apiError?.details || 'Could not send the message.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <section className="bg-white border rounded-2xl p-6 space-y-4">
        <div>
          <h2 className="text-xl font-bold">Send notice or announcement</h2>
          <p className="text-sm text-gray-500 mt-1">Warnings and notices remain until “I understand.” Announcements stop after acknowledgement, duration, or display limit.</p>
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} className="w-full border rounded-lg px-3 py-2">
          <option value="announcement">Announcement</option>
          <option value="warning">Warning</option>
          <option value="notice">Notice</option>
        </select>
        <input maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="w-full border rounded-lg px-3 py-2" />
        <textarea maxLength={500} rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message" className="w-full border rounded-lg px-3 py-2" />

        <div className="flex gap-4 text-sm font-semibold">
          <label><input type="radio" className="mr-2" checked={targetScope === 'all'} onChange={() => setTargetScope('all')} />Entire app</label>
          <label><input type="radio" className="mr-2" checked={targetScope === 'selected'} onChange={() => setTargetScope('selected')} />Selected users</label>
        </div>

        {targetScope === 'selected' && (
          <div>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, username, email, or user ID" className="w-full border rounded-lg px-3 py-2" />
            {searchResults.length > 0 && <div className="border rounded-lg mt-1 divide-y max-h-48 overflow-y-auto">
              {searchResults.map((user) => <button type="button" key={user._id} onClick={() => addUser(user)} className="w-full text-left p-2 hover:bg-gray-50 text-sm">
                <strong>{user.profileName || user.username || 'Unnamed user'}</strong> <span className="text-gray-500">{user.email}</span>
              </button>)}
            </div>}
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedUsers.map((user) => <button type="button" key={user._id} onClick={() => setSelectedUsers((items) => items.filter((item) => item._id !== user._id))} className="bg-pink-50 text-pink-700 rounded-full px-3 py-1 text-xs font-semibold">
                {user.profileName || user.username || user.email} ×
              </button>)}
            </div>
          </div>
        )}

        {type === 'announcement' && <>
          <input type="url" maxLength={2048} value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="Optional image URL" className="w-full border rounded-lg px-3 py-2" />
          <input type="url" maxLength={2048} value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="Optional link URL" className="w-full border rounded-lg px-3 py-2" />
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm font-semibold">Duration (hours)<input type="number" min="1" max="8760" value={durationHours} onChange={(e) => setDurationHours(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2 font-normal" /></label>
            <label className="text-sm font-semibold">Show up to<input type="number" min="1" max="100" value={maxDisplays} onChange={(e) => setMaxDisplays(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2 font-normal" /></label>
          </div>
        </>}

        {error && <div className="bg-red-50 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
        <button type="button" disabled={busy || !title.trim() || !message.trim() || (targetScope === 'selected' && !selectedUsers.length)} onClick={submit} className="w-full bg-pink-600 disabled:bg-gray-300 text-white rounded-xl py-3 font-bold">
          {busy ? 'Sending…' : targetScope === 'all' ? 'Send to entire app' : `Send to ${selectedUsers.length} user${selectedUsers.length === 1 ? '' : 's'}`}
        </button>
      </section>

      <section className="bg-white border rounded-2xl p-6">
        <h2 className="text-xl font-bold mb-4">Recent sends</h2>
        <div className="space-y-3 max-h-[720px] overflow-y-auto">
          {campaigns.map((notice) => <div key={notice._id} className="border rounded-xl p-3 text-sm">
            <div className="flex justify-between gap-3"><strong>{notice.type === 'warning' ? '⚠️' : notice.type === 'announcement' ? '📣' : 'ℹ️'} {notice.title}</strong><span className="text-xs text-gray-500">{notice.targetScope === 'all' ? 'Entire app' : `${notice.targetUserIds?.length || 0} users`}</span></div>
            <p className="text-gray-600 mt-1">{notice.message}</p>
            <p className="text-xs text-gray-400 mt-2">Sent {formatDate(notice.createdAt)}{notice.expiresAt ? ` · Ends ${formatDate(notice.expiresAt)} · Up to ${notice.maxDisplays} displays` : ' · Until acknowledged'}</p>
            {!notice.revokedAt && <button type="button" className="text-red-600 font-semibold mt-2" onClick={async () => { await adminAPI.revokeAnnouncement(notice._id); await loadCampaigns(); }}>Revoke</button>}
            {notice.revokedAt && <span className="block text-red-500 mt-2">Revoked</span>}
          </div>)}
          {!campaigns.length && <p className="text-gray-500">No announcements sent yet.</p>}
        </div>
      </section>
    </div>
  );
}
